import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Skull, Loader2, CheckCircle2, XCircle, StarOff, ChevronDown } from "lucide-react";

const STAR_VALUES = [-1, -2, -3, -4, -5];
const STAR_LABELS = {
  "-1": "Málem dobré",
  "-2": "Docela trauma",
  "-3": "Slušný záhul",
  "-4": "Chci zapomenout",
  "-5": "Naprostá katastrofa",
};

function StarRow({ value, active }) {
  const n = Math.abs(value);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarOff key={i} className={`w-5 h-5 ${i <= n ? (active ? "text-nv drop-shadow-[0_0_5px_rgba(74,222,128,0.6)]" : "text-nv/70") : "text-neutral-800"}`} strokeWidth={2} />
      ))}
    </span>
  );
}

export default function Rate2Page() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | found | notfound | sending | done
  const [cfg, setCfg] = useState(null);
  const [nickname, setNickname] = useState("");
  const [stars, setStars] = useState(null);
  const [text, setText] = useState("");
  const [locked, setLocked] = useState(false);
  const [openArea, setOpenArea] = useState(null);

  useEffect(() => {
    api.get(`/reviews2/link/${token}`)
      .then(({ data }) => {
        if (!data.valid) return setState("notfound");
        setCfg(data);
        setState("found");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  const pickTemplate = (variant) => { setText(variant); setLocked(true); };
  const writeOwn = () => { setText(""); setLocked(false); setOpenArea(null); };
  const starLabel = (v) => (cfg?.star_labels && cfg.star_labels[Math.abs(v) - 1]) || STAR_LABELS[String(v)];

  const submit = async () => {
    if (!stars) return;
    setState("sending");
    try {
      await api.post("/reviews2/submit", { token, nickname, stars, text });
      setState("done");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) { setState("notfound"); }
      else { toast.error("Odeslání se nezdařilo, zkus to prosím znovu."); setState("found"); }
    }
  };

  return (
    <div className="grain-layer min-h-screen bg-void flex items-center justify-center px-5 py-12 relative text-neutral-200">
      <div className="absolute inset-0 scanlines pointer-events-none" />
      <div className="relative z-10 w-full max-w-lg border border-white/10 bg-[#0a0a0a] p-8 border-t-2 border-t-nv" data-testid="rate2-page">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Skull className="w-6 h-6 text-red-600" />
          <h1 className="font-head text-xl uppercase tracking-widest text-neutral-100">{cfg?.title || "Negativní recenze"}</h1>
        </div>
        <p className="text-center text-xs tracking-[0.3em] text-nv uppercase mb-6">{cfg?.subtitle || "Čím víc mínus, tím líp"}</p>

        {state === "loading" && <Loader2 className="w-10 h-10 text-nv animate-spin mx-auto my-8" />}

        {state === "notfound" && (
          <div className="py-6 text-center" data-testid="rate2-notfound">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-neutral-400">Odkaz na hodnocení není platný nebo vypršel.</p>
          </div>
        )}

        {(state === "found" || state === "sending") && cfg && (
          <div className="space-y-5">
            {cfg.intro ? <p className="text-sm text-neutral-400 leading-relaxed whitespace-pre-line">{cfg.intro}</p> : null}

            <div>
              <label className="text-xs tracking-widest text-neutral-500 uppercase">{cfg.nick_label || "Přezdívka"}</label>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={cfg.nick_placeholder || "Např. PřeživšíZPodkroví"}
                maxLength={60}
                data-testid="rate2-nickname"
                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-neutral-200 focus:border-nv outline-none mt-1"
              />
            </div>

            <div>
              <label className="text-xs tracking-widest text-neutral-500 uppercase">{cfg.stars_label || "Kolik mínus hvězd?"}</label>
              <div className="space-y-2 mt-2">
                {STAR_VALUES.map((v) => (
                  <button
                    key={v}
                    onClick={() => setStars(v)}
                    data-testid={`rate2-star-${v}`}
                    className={`w-full text-left border p-3 flex items-center justify-between gap-4 transition-colors ${stars === v ? "border-nv bg-nv/10 text-neutral-100" : "border-white/10 bg-[#0b0a09] text-neutral-300 hover:border-nv/40"}`}
                  >
                    <span className="font-head text-sm">{starLabel(v)}</span>
                    <StarRow value={v} active={stars === v} />
                  </button>
                ))}
              </div>
            </div>

            {cfg.templates && cfg.templates.length > 0 && !locked && (
              <div>
                <label className="text-xs tracking-widest text-neutral-500 uppercase">Rychlá recenze (nemusíš psát)</label>
                <div className="mt-2 space-y-2" data-testid="rate2-templates">
                  {cfg.templates.map((t, ai) => (
                    <div key={ai} className="border border-white/10 bg-[#0b0a09]">
                      <button
                        onClick={() => setOpenArea(openArea === ai ? null : ai)}
                        data-testid={`rate2-area-${ai}`}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-neutral-200 hover:text-nv"
                      >
                        <span className="font-head">{t.area}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${openArea === ai ? "rotate-180 text-nv" : ""}`} />
                      </button>
                      {openArea === ai && (
                        <div className="border-t border-white/10 p-2 space-y-2">
                          {(t.variants || []).map((v, vi) => (
                            <button
                              key={vi}
                              onClick={() => pickTemplate(v)}
                              data-testid={`rate2-variant-${ai}-${vi}`}
                              className="w-full text-left text-sm text-neutral-400 border border-white/10 p-2 hover:border-nv/40 hover:text-neutral-200 transition-colors"
                            >
                              „{v}"
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs tracking-widest text-neutral-500 uppercase">{locked ? `${cfg.text_label || "Text recenze"} (z šablony)` : (cfg.text_label || "Text recenze (nepovinné)")}</label>
                {locked && (
                  <button onClick={writeOwn} data-testid="rate2-write-own" className="text-[11px] text-nv hover:underline">Napsat vlastní</button>
                )}
              </div>
              <textarea
                value={text}
                onChange={(e) => !locked && setText(e.target.value)}
                readOnly={locked}
                maxLength={1500}
                placeholder={cfg.text_placeholder || "Napiš nám tu nejhorší recenzi…"}
                data-testid="rate2-text"
                rows={4}
                className={`w-full bg-black border border-white/15 px-3 py-2 text-sm text-neutral-200 focus:border-nv outline-none mt-1 ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
              />
            </div>

            <button
              onClick={submit}
              disabled={!stars || state === "sending"}
              data-testid="rate2-submit-btn"
              className="hud-btn vhs-btn w-full font-head tracking-[0.2em] py-4 text-sm disabled:opacity-40"
            >
              {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (cfg.submit_label || "[ ODESLAT HODNOCENÍ ]")}
            </button>
            <p className="text-[11px] text-neutral-600 text-center">{cfg.note || "Hodnocení projde schválením, než se objeví na webu."}</p>
          </div>
        )}

        {state === "done" && (
          <div className="py-6 text-center" data-testid="rate2-done">
            <CheckCircle2 className="w-14 h-14 text-nv mx-auto mb-4 glow-nv" />
            <h3 className="font-head text-xl uppercase text-neutral-100 mb-2">Díky za hodnocení!</h3>
            <p className="text-sm text-neutral-400">{cfg?.thanks || "Tvoje recenze čeká na schválení, než se objeví na webu."}</p>
            <a href="/" className="inline-block mt-6 hud-btn vhs-btn px-6 py-3 font-head text-sm">Zpět na web</a>
          </div>
        )}
      </div>
    </div>
  );
}
